<template>
  <UCPreload
    :show="isLoadingConsumptions"
    :number="1"
  />

  <div
    v-if="!isLoadingConsumptions"
    class="container-table"
  >
    <table>
      <!-- Table header -->
      <thead>
        <tr>
          <th
            scope="col"
            class="padding-left-half"
          >
            {{ $t('common.warehouse') }}
          </th>
          <th scope="col">
            {{ $t('common.article') }}
          </th>
          <th scope="col">
            {{ $t('common.name') }}
          </th>
          <th scope="col">
            {{ $t('common.quantity') }}
          </th>
        </tr>
      </thead>

      <!-- Table data -->
      <tbody>
        <template v-if="expectedConsumptions && expectedConsumptions.length > 0">
          <tr
            v-for="(item, i) in expectedConsumptions"
            :key="i"
          >
            <td class="text-align-center">
              {{ item.Almacen ? item.Almacen.Nombre : '—' }}
            </td>
            <td class="text-align-center">
              {{ item.Articulo ? item.Articulo.Codigo : '' }}
            </td>
            <td class="text-align-center">
              {{ item.Articulo ? item.Articulo.Nombre : '' }}
            </td>
            <td class="text-align-center">
              {{ item.Cantidad }}
            </td>
          </tr>
        </template>

        <!-- No data to show -->
        <template v-else>
          <tr>
            <td
              colspan="5"
              class="text-align-center"
            >
              No hay datos
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup>
// @ts-check

import { onMounted, ref, watch } from 'vue';
import { useRepairParts } from '@/composables/useRepairParts';
import { logError } from '@/js/utils/log';
import { callFetchExpectedConsumptions } from '@/js/services/repairs/ApiRepairs';

import UCPreload from '@/components/common/UCPreload.vue';

/** @import { Ref } from 'vue' */

/** @import { ParteProduccionPrevistoEntrada } from '@erpagroapp/types' */

const isLoadingConsumptions = ref(true);

/** @type {Ref<ParteProduccionPrevistoEntrada[]>} */
const expectedConsumptions = ref([]);

const { workPartDetails } = useRepairParts();

/**
 * Fetches expected consumption articles.
 * @param {number} workPartId - The ID of the work part.
 */
async function fetchExpectedConsumptions(workPartId) {
  try {
    const { data } = await callFetchExpectedConsumptions(workPartId);
    expectedConsumptions.value = data;
  } catch (error) {
    logError(error);
  } finally {
    isLoadingConsumptions.value = false;
  }
}

watch(workPartDetails, () => {
  if (workPartDetails.value) {
    fetchExpectedConsumptions(workPartDetails.value.Id_ParteProduccion);
  }
});

onMounted(() => {
  if (!workPartDetails.value) {
    return;
  }

  fetchExpectedConsumptions(workPartDetails.value.Id_ParteProduccion);
});
</script>

<style scoped lang="scss">
.container-table {
  overflow-x: auto;
  padding: calc(var(--f7-typography-padding) / 2);

  table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;

    thead {
      background-color: #9e9e9e;

      th {
        background-color: #9e9e9e;
        padding: 5px;
      }

      th:first-child {
        padding-left: calc(var(--f7-typography-padding) / 2);
        border-top-left-radius: 5px;
      }

      th:last-child {
        padding-right: calc(var(--f7-typography-padding) / 2);
        border-top-right-radius: 5px;
      }
    }

    tbody {
      background-color: #e6e6e6;

      td {
        padding: 5px;
      }

      td:first-child {
        padding-left: calc(var(--f7-typography-padding) / 2);
      }

      td:last-child {
        padding-right: calc(var(--f7-typography-padding) / 2);
      }
    }
  }
}
</style>
